import React, { useState } from "react";
import { useNotify, useTranslate, useGetList } from "react-admin";
import {
  Container,
  Box,
  TextField as MuiTextField,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "../../providers/firebase";
import ConfirmDialog from "../../layout/Dialog";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const NotificationPage = () => {
  const translate = useTranslate();
  const notify = useNotify();

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState<"specific" | "active">("specific");
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false); // ⬅️ dialog state

  const {
    data: userOptions = [],
    isLoading,
    error,
  } = useGetList<User>("users", {
    pagination: { page: 1, perPage: 25 },
    sort: { field: "email", order: "DESC" },
    filter: searchTerm
      ? {
          email_gte: searchTerm,
          email_lte: searchTerm + "\uf8ff",
          deviceToken_neq: null,
        }
      : {},
  });

  // ⬇️ actual push function (called *after* user confirms)
  const sendPushNotification = async () => {
    try {
      const auth = getAuth(firebaseApp);
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated.");

      const token = await user.getIdToken();

      const payload = {
        title,
        message,
        type: target === "specific" ? "users" : "all",
        userIds:
          target === "specific" ? selectedUsers.map((u) => u.id) : undefined,
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${import.meta.env.VITE_SYNC_API}/push`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        notify("Push notification sent successfully", { type: "success" });
        setTitle("");
        setMessage("");
        setSelectedUsers([]);
      } else {
        notify(`Push API returned an error: ${response.status}`, {
          type: "error",
        });
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.warn("Push API request timed out.");
      } else {
        notify(`Error sending push notification: ${error.message}`, {
          type: "error",
        });
      }
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setDialogOpen(true); // ⬅️ open confirmation dialog
  };

  const handleConfirm = async () => {
    setDialogOpen(false);
    await sendPushNotification();
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };
  // inside your NotificationPage component

  // Build a string with selected emails when sending to specific users
  const selectedEmails =
    target === "specific" && selectedUsers.length > 0
      ? selectedUsers.map((user) => user.email).join(", ")
      : "";

  const confirmDescription =
    target === "specific" && selectedEmails
      ? `${translate(
          "notificationsPage.confirmDesc"
        )} \nSelected Emails: ${selectedEmails}`
      : translate("notificationsPage.confirmDescSendAll");

  return (
    <Container maxWidth="md" sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {translate("notificationsPage.notifications")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <MuiTextField
          fullWidth
          required
          label={translate("notificationsPage.title")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          inputProps={{ maxLength: 50 }}
          helperText={`${title.length}/50`}
        />

        <MuiTextField
          fullWidth
          required
          multiline
          rows={4}
          label={translate("notificationsPage.message")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          margin="normal"
          inputProps={{ maxLength: 150 }}
          helperText={`${message.length}/150`}
        />

        <FormControl component="fieldset" margin="normal">
          <FormLabel>
            {translate("notificationsPage.selectRecipients")}
          </FormLabel>
          <RadioGroup
            row
            value={target}
            onChange={(e) => setTarget(e.target.value as "specific" | "active")}
          >
            <FormControlLabel
              value="specific"
              control={<Radio />}
              label={translate("notificationsPage.specificUsers")}
            />
            <FormControlLabel
              value="active"
              control={<Radio />}
              label={translate("notificationsPage.boradcastToAll")}
            />
          </RadioGroup>
        </FormControl>

        {target === "specific" && (
          <Autocomplete
            multiple
            options={userOptions}
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName} (${option.email})`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedUsers}
            onChange={(_, newValue) => {
              setSelectedUsers(newValue);
              setSearchTerm("");
            }}
            onInputChange={(_, value) => setSearchTerm(value)}
            loading={isLoading}
            disabled={Boolean(error)}
            filterSelectedOptions
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {`${option.firstName} ${option.lastName} (${option.email})`}
              </li>
            )}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label={translate("notificationsPage.selectUsers")}
                placeholder={translate("notificationsPage.searchUsers")}
                margin="normal"
                helperText={
                  error ? translate("notificationsPage.errorLoadingUsers") : ""
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}

        <Box mt={2}>
          <Button
            variant="contained"
            type="submit"
            disabled={
              !title.trim() ||
              !message.trim() ||
              (target === "specific" && selectedUsers.length === 0)
            }
          >
            {translate("notificationsPage.sendNotification")}
          </Button>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={dialogOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={translate("notificationsPage.confirmTitle")}
        description={confirmDescription}
        cancelText={translate("notificationsPage.cancel")}
        confirmText={translate("notificationsPage.confirm")}
      />
    </Container>
  );
};

export default NotificationPage;
